import { Button, Link, TextField, WithStyles } from '@material-ui/core';
import { CloudUpload, Domain, GetApp, SearchOutlined } from '@material-ui/icons';
import { createStyles, withStyles } from '@material-ui/styles';
import { Autocomplete, Stack } from '@mui/material';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import { PaginatedDataDTO, PaginationDTO } from '@bottega52/commons-pagination';
import _ from 'lodash';
import 'moment/locale/it';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { IMksImportInDTO } from '../../repository/jago/model/input/IMksImportInDTO';
import { ICsvPreviewInDTO } from '../../repository/jago/model/input/ICsvPreviewInDTO';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { IWalletPlantsRequestParamsDTO, IMksImportsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import * as MksActions from '../../redux/mks/mks.actions';
import * as CreditsActions from '../../redux/credits/credits.actions';
import { IState } from '../../redux/store';
import ReduxLanguage from '../../components/ReduxLanguage';
import CsvUploadModal from '../../components/Modals/CsvUploadModal';
import CsvPreviewModal from '../../components/Modals/CsvPreviewModal';
import CsvConfirmationModal from '../../components/Modals/CsvConfirmationModal';
import translations from '../../translations/i18next';
import { getDomainsOptions, getDomainNameFromHostname } from '../../repository/utils';
import ImportsFilterBar from '../../components/SearchBar/ImportsFilterBar';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import Permission from '../../components/Permission/Permission';
import * as ModalsActions from '../../redux/modals/modals.actions';
import { ModalTypes } from '../../components/Modals/ModalTypes';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  text: {
    margin: 0,
    marginBottom: 15,
  },
  logo: {
    width: '20%',
  },
  innerHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  header: {
    backgroundColor: 'white',
    width: '100%',
    zIndex: 200,
    padding: 10,
    paddingRight: 20,
    borderBottom: '2px solid #5AC0B1',
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 160px)',
    width: '100%'
  },
  statusChip: {
    padding: '4px 8px',
    borderRadius: 4,
    fontSize: '12px',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  statusSuccess: {
    backgroundColor: 'gray',
    color: 'white',
  },
  statusError: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  statusPending: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  statusCurrent: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
});


export interface IMksImportsPageProps extends WithStyles<typeof styles> {
  language: string;
  imports: PaginatedDataDTO<IMksImportInDTO>;
  csvPreview: ICsvPreviewInDTO;
  selectedHostname: string;
  selectedDomain: IDomainOptionDTO | null;
  lastImport: IMksImportInDTO | {};
}

export interface IMksImportsPageState {
  domainsOptions: IDomainOptionDTO[];
  rowsPerPage: number;
  isLoading: boolean;
  showUploadModal: boolean;
  showPreviewModal: boolean;
  showConfirmationModal: boolean;
  selectedFile: File | null;
  selectedFileFormat: string;
  uploadError: string;
  domainSearchText: string;
}

function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    imports: state.mks.imports.data,
    csvPreview: state.mks.csvPreview,
    selectedHostname: state.mks.selectedHostname,
    selectedDomain: state.mks.selectedDomain,
    lastImport: state.mks.imports.lastImport,
  };
}

const connector = connect(mapStateToProps);
type IReduxProps = ConnectedProps<typeof connector>;

export class MksImportsPage extends React.Component<IMksImportsPageProps & IReduxProps, IMksImportsPageState> {
  private importsColumns: GridColumns;

  constructor(props: IMksImportsPageProps & IReduxProps) {
    super(props);
    this.state = {
      domainsOptions: [],
      rowsPerPage: 100,
      isLoading: false,
      showUploadModal: false,
      showPreviewModal: false,
      showConfirmationModal: false,
      selectedFile: null,
      uploadError: '',
      domainSearchText: '',
    };

    this.importsColumns = [
      { 
        field: 'id',
        headerName: 'Id',
        flex: 1,
      },
      { 
        field: 'filename', 
        headerName: translations.t('mks.filename'), 
        type: 'string', 
        sortable: false, 
        filterable: false, 
        minWidth: 200, 
        flex: 4, 
        renderCell: (params: GridRenderCellParams<any>) => { 
          const importItem = params.row; 
          return (
            <div>
              <h3 style={{ marginLeft: 0, fontWeight: 'bold' }}>{importItem.filename}</h3> 
              <h4 style={{ marginLeft: 0, fontWeight: 'normal' }}>{importItem.fileType ? _.startCase(importItem.fileType.toLowerCase()) : ''}</h4>
          </div>
          )
        } 
      },
      { 
        field: 'importTimestamp', 
        headerName: translations.t('mks.importTimestamp'), 
        type: 'string', 
        sortable: false, 
        filterable: false, 
        minWidth: 150, 
        flex: 2,
        renderCell: (params: GridRenderCellParams<any>) => {
          const timestamp = params.row.importTimestamp;
          return timestamp ? timestamp.replace('T', ' ') : '';
        }
      },
      { 
        field: 'importStatus', 
        headerName: translations.t('mks.status'), 
        type: 'string', 
        sortable: false, 
        filterable: false, 
        minWidth: 120, 
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          const status = params.row.importStatus;
          const { classes } = this.props;
          let statusClass = classes.statusPending;
          let displayStatus = status;
          
          // Se è la prima riga (indice 0) e lo status è COMPLETED, mostra CURRENT
          if (params.api.getRowIndexRelativeToVisibleRows(params.id) === 0 && status === 'COMPLETED') {
            displayStatus = 'CURRENT';
            statusClass = classes.statusCurrent;
            displayStatus = translations.t('mks.status_CURRENT').toUpperCase();
          } else if (status === 'COMPLETED') {
            statusClass = classes.statusSuccess;
            displayStatus = translations.t('mks.status_COMPLETED').toUpperCase();
          } else if (status === 'ERROR') {
            displayStatus = translations.t('mks.status_ERROR').toUpperCase();
            statusClass = classes.statusError;
          }
          
          return <div className={`${classes.statusChip} ${statusClass}`}>{displayStatus}</div>
        }
      },
      { 
        field: 'userEmail', 
        headerName: translations.t('mks.userEmail'), 
        type: 'string', 
        sortable: false, 
        filterable: false, 
        minWidth: 200, 
        flex: 2 
      },
      { 
        field: 'keysImported', 
        headerName: translations.t('mks.keys'), 
        type: 'number', 
        sortable: false, 
        filterable: false, 
        minWidth: 80, 
        flex: 1 
      },
      { 
        field: 'cylindersImported', 
        headerName: translations.t('mks.cylinders'), 
        type: 'number', 
        sortable: false, 
        filterable: false, 
        minWidth: 80, 
        flex: 1 
      },
      { 
        field: 'relationsImported', 
        headerName: translations.t('mks.relations'), 
        type: 'number', 
        sortable: false, 
        filterable: false, 
        minWidth: 80, 
        flex: 1 
      },
      { 
        field: 'actions', 
        headerName: translations.t('forms.actions'), 
        type: 'actions', 
        sortable: false, 
        filterable: false, 
        minWidth: 100, 
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          const importItem = params.row;
          const hasDocuments = importItem.documents && Object.keys(importItem.documents).length > 0;
          
          if (!hasDocuments) {
            return <div style={{ padding: '8px', color: '#999' }}>-</div>;
          }
          
          return (
            <Button
              variant="contained"
              size="small"
              style={{ 
                backgroundColor: '#5AC0B1', 
                color: 'white', 
                minWidth: 'auto',
                padding: '4px 8px',
                fontSize: '12px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                this.onDownloadCsv(importItem);
              }}
            >
              <GetApp style={{ fontSize: 16 }} />
              
            </Button>
          );
        }
      },
    ];
  }

  public onDownloadCsv(importItem: IMksImportInDTO) {
    if (!importItem.documents || Object.keys(importItem.documents).length === 0) {
      return;
    }
    
    // Prendi la prima chiave dei documents (il CSV)
    const firstDocumentKey = Object.keys(importItem.documents)[0];
    const csvContent = importItem.documents[firstDocumentKey];
    
    if (!csvContent) {
      return;
    }
    window.open(csvContent,'_blank');
  }

  public async componentDidMount() {
    await this.fetchDomainsOptions('');
    
    const { selectedDomain, dispatch } = this.props;
    if (selectedDomain) {
      dispatch<any>(MksActions.fetchMechanicalCylinderKeyRelations({
        hostname: selectedDomain.value.hostname,
        page: 0,
        pageSize: 10000,
      }));
      this.fetchImportsForSelectedDomain();
    } else {
      // Check localStorage for saved domain
      const savedHostname = localStorage.getItem('masterKeyDomain');
      if (savedHostname) {
        const params: IWalletPlantsRequestParamsDTO = { 
          hostname: savedHostname,
          page: 0,
          pageSize: 50,
          walletId: undefined
        };
       
        const domainsResponse = await dispatch<any>(CreditsActions.fetchDomainsSearch(params));
        if (domainsResponse && domainsResponse.length > 0) {
          const domainsOptions = getDomainsOptions(domainsResponse);
          if (domainsOptions && domainsOptions.length > 0) {
            const domainOption = domainsOptions[0];
            dispatch<any>(MksActions.setSelectedDomain(domainOption));
            this.setState({ domainSearchText: domainOption.label, domainsOptions }, () => {
              this.fetchImportsForSelectedDomain();
            });
          }
        }
      }
    }
  }

  public onSelectDomainFromEmptyState(domain: IDomainOptionDTO | null) {
    const { dispatch } = this.props;
    
    if (domain) {
      dispatch<any>(MksActions.setSelectedDomain(domain));
      // Save domain hostname in localStorage
      localStorage.setItem('masterKeyDomain', domain.value.hostname);
      this.setState({ domainSearchText: domain.label }, () => {
        this.fetchImportsForSelectedDomain();
      });
    } else {
      localStorage.removeItem('masterKeyDomain');
      this.setState({ domainSearchText: '' });
    }
  }

  public onFetchDomainsFromEmptyState(value: string) {
    this.fetchDomainsOptions(value);
  }

  private async fetchImportsForSelectedDomain() {
    const { selectedDomain, dispatch } = this.props;
    if (!selectedDomain) return;

    const params: IMksImportsRequestParamsDTO = {
      hostname: selectedDomain.value.hostname,
      page: 0,
      pageSize: 100,
    };
    dispatch<any>(MksActions.resetMksImportsData());
    await this.onSearch(params);
    await this.fetchLastImport();
  }

  public async onSearch(params: IMksImportsRequestParamsDTO) {
    const { dispatch } = this.props;
    try {
      this.setState({ isLoading: true });
      await dispatch<any>(MksActions.fetchMksImports(params));
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  }

  private async fetchLastImport() {
    const { dispatch, selectedDomain } = this.props;
    if (!selectedDomain) return;

    try {
      const params: IMksImportsRequestParamsDTO = {
        hostname: selectedDomain.value.hostname,
        page: 0,
        pageSize: 1,
      };
      await dispatch<any>(MksActions.fetchLastMksImport(params));
    } catch (error) {
      // Gestisci errore silenziosamente
    }
  }

  public onResetFilters() {
    const { dispatch } = this.props;
    dispatch<any>(MksActions.setSelectedDomain(null));
    localStorage.removeItem('masterKeyDomain');
    this.setState({ domainSearchText: '' });
  }

  public onDomainSelect(domain: IDomainOptionDTO | null) {
    const { dispatch } = this.props;
    dispatch<any>(MksActions.setSelectedDomain(domain));
    
    // Se viene deselezionato il dominio, resetta anche il testo di ricerca centrale e localStorage
    if (!domain) {
      this.setState({ domainSearchText: '' });
      localStorage.removeItem('masterKeyDomain');
    } else {
      // Save domain hostname in localStorage
      localStorage.setItem('masterKeyDomain', domain.value.hostname);
      this.setState({ domainSearchText: domain.label }, () => {
        this.fetchImportsForSelectedDomain();
      });
    }
  }

  public async fetchDomainsOptions(value: string) {
    const { dispatch } = this.props;
    try {
      const params: IWalletPlantsRequestParamsDTO = { 
        hostname: value,
        page: 0,
        pageSize: 50,
        walletId: undefined
      };
      const domainsResponse = await dispatch<any>(CreditsActions.fetchDomainsSearch(params));
      if (domainsResponse) {
        const domainsOptions = getDomainsOptions(domainsResponse);
          this.setState({ domainsOptions });
        }
    } catch (error) {
      console.error('Error fetching domains:', error);
    }
  }

  public onOpenUploadModal() {
    const canImportCsv = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.MKS_CSV_IMPORT);
    if (!canImportCsv) {
      this.props.dispatch<any>(ModalsActions.showModal(`ERROR_MKS_CSV_IMPORT`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'mks.yourRoleDoesNotHavePermissionToImportCsv',
        }
      }));
      return;
    }; 
    this.setState({ showUploadModal: true, uploadError: '' });
  }

  public onCloseUploadModal() {
    this.setState({ showUploadModal: false, selectedFile: null, selectedFileFormat: '', uploadError: '' });
  }

  public onFileSelect(file: File) {
    this.setState({ selectedFile: file });
  }

  public onFileFormatSelect(fileFormat: string) {
    this.setState({ selectedFileFormat: fileFormat });
  }

  public async onPreviewCsv() {
    const { selectedFile, selectedFileFormat } = this.state;
    const { selectedDomain, dispatch } = this.props;
    
    if (!selectedFile || !selectedDomain || !selectedFileFormat) return;

    try {
      this.setState({ isLoading: true, uploadError: '' });
      await dispatch<any>(MksActions.previewCsvData(selectedDomain.value.hostname, selectedFileFormat, selectedFile));
      this.setState({ isLoading: false, showUploadModal: false, showPreviewModal: true });
    } catch (error) {
      this.setState({ isLoading: false, uploadError: translations.t('errors.errorPreviewingFile') });
    }
  }

  public onClosePreviewModal() {
    const { dispatch } = this.props;
    this.setState({ showPreviewModal: false, selectedFile: null });
    dispatch<any>(MksActions.clearCsvPreview());
  }

  public onCloseConfirmationModal() {
    this.setState({ showConfirmationModal: false });
  }

  public onConfirmImport() {
    // Apri la terza dialog di conferma invece di eseguire direttamente l'import
    // A meno che non ci sia neanche un import precedente, in quel caso procediamo direttamente con l'import
    const { lastImport } = this.props;
    if (!lastImport || _.isEmpty(lastImport)) {
      this.onFinalConfirmImport();
    } else {
      this.setState({ showPreviewModal: false, showConfirmationModal: true });
    }
  }

  public async onFinalConfirmImport() {
    const { selectedFile, selectedFileFormat } = this.state;
    const { selectedDomain, dispatch } = this.props;
    
    if (!selectedFile || !selectedDomain || !selectedFileFormat) return;

    try {
      this.setState({ isLoading: true });
      await dispatch<any>(MksActions.importMechanicalCylindersFromCsv(selectedDomain.value.hostname, selectedFileFormat, selectedFile));
      this.setState({ isLoading: false, showConfirmationModal: false, selectedFile: null });
      // Ricarica la lista degli import
      this.fetchImportsForSelectedDomain();
      dispatch<any>(MksActions.fetchCylinders({
        hostname: selectedDomain.value.hostname,
        page: 0,
        pageSize: 100,
      }));
      dispatch<any>(MksActions.fetchKeys({
        hostname: selectedDomain.value.hostname,
        page: 0,
        pageSize: 100,
      }));
      dispatch<any>(MksActions.fetchMechanicalCylinderKeyRelations({
        hostname: selectedDomain.value.hostname,
        page: 0,
        pageSize: 10000,
      }));
    } catch (error) {
      this.setState({ isLoading: false });
      // Gestisci errore import
    }
  }

  public handleChangeRowsPerPage(pageSize: number) {
    this.setState({ rowsPerPage: pageSize });
  }

  public onFetchImportsPerPage(page: number) {
    const { selectedDomain } = this.props;
    const { rowsPerPage } = this.state;
    
    if (!selectedDomain) return;

    const params: IMksImportsRequestParamsDTO = {
      hostname: selectedDomain.value.hostname,
      page: page,
      pageSize: rowsPerPage,
    };
    this.onSearch(params);
  }

  public render() {
    const { classes, imports, csvPreview, selectedHostname, selectedDomain } = this.props;
    const { isLoading, domainsOptions, showUploadModal, showPreviewModal, showConfirmationModal, selectedFile, uploadError, rowsPerPage, domainSearchText } = this.state;

    const hasPreview = csvPreview && Object.keys(csvPreview).length > 0;


    return (
      <div className={classes.container}>
        <div className={classes.header} style={{ backgroundColor: 'white' }}>
          <ImportsFilterBar
            onSearch={(params) => this.onSearch(params)}
            onResetFilters={() => this.onResetFilters()}
            onFilterDomains={(value) => this.fetchDomainsOptions(value)}
            onDomainSelect={(domain) => this.onDomainSelect(domain)}
            onUploadCsv={() => this.onOpenUploadModal()}
            domainsOptions={domainsOptions}
            selectedHostname={selectedHostname}
            selectedDomain={selectedDomain}
            lastImport={this.props.lastImport}
          />
        </div>
        <div className={classes.tableContainer}>

          {selectedHostname ? (
            <DataGrid
              getRowId={(row) => row.importTimestamp + row.filename}
              rows={imports.content || []}
              columns={this.importsColumns}
              rowHeight={70}
              loading={this.state.isLoading}
              onPageSizeChange={(pageSize) => this.handleChangeRowsPerPage(pageSize)}
              onPageChange={page => this.onFetchImportsPerPage(page)}
              page={(imports?.pagination as PaginationDTO)?.number}
              rowCount={(imports?.pagination as PaginationDTO)?.totalElements}
              pageSize={rowsPerPage}
              paginationMode="server"
              sortingMode="server"
              disableColumnMenu
              disableColumnSelector
              componentsProps={{
                pagination: {
                  labelRowsPerPage: translations.t('forms.rowsPerPage')
                }
              }}
              components={{
                NoRowsOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center">
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                      <SearchOutlined style={{ alignSelf: 'center', marginBottom: '16px' }} />
                      <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                        <ReduxLanguage languageKey='mks.noImportsFoundTitle' />
                      </h3>
                      <h4 style={{ textAlign: 'center', marginTop: 0, marginBottom: '24px' }}>
                        <ReduxLanguage languageKey='mks.noImportsFoundDescription' />
                      </h4>
                      <Button
                        variant="contained"
                        style={{ backgroundColor: 'cornflowerblue', color: 'white', marginTop: 16, fontWeight: 'bold', zIndex: 999, position: 'relative' }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          this.onOpenUploadModal();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          this.onOpenUploadModal();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          this.onOpenUploadModal();
                        }}
                      >
                        <CloudUpload style={{ marginRight: 10 }} />
                        <ReduxLanguage languageKey="mks.uploadCsv" />
                      </Button>
                    </div>
                  </Stack>
                ),
                NoResultsOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center">
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                      <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                        <ReduxLanguage languageKey='mks.noImportsFoundTitle' />
                      </h3>
                      <h4 style={{ textAlign: 'center', marginTop: 0, marginBottom: '24px' }}>
                        <ReduxLanguage languageKey='mks.noImportsFoundDescription' />
                      </h4>
                        <Button
                          variant="contained"
                          style={{ backgroundColor: 'cornflowerblue', color: 'white', marginTop: 16, fontWeight: 'bold', zIndex: (showUploadModal || showPreviewModal || showConfirmationModal) ? 'auto' : 9999, position: 'relative' }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            this.onOpenUploadModal();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            this.onOpenUploadModal();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            this.onOpenUploadModal();
                          }}
                        >
                          <CloudUpload style={{ marginRight: 10 }} />
                          <ReduxLanguage languageKey="mks.uploadCsv" />
                        </Button>
                    </div>
                  </Stack>
                )
              }}
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
          ) : (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                <Domain style={{ fontSize: 40, marginBottom: 10 }} />
                <h2 style={{ textAlign: 'center', fontWeight: 'bold' }}>
                  <ReduxLanguage languageKey='mks.hostnameRequiredTitle' />
                </h2>
                <h3 style={{ textAlign: 'center', marginTop: 10, marginBottom: '24px' }}>
                  <ReduxLanguage languageKey='mks.hostnameRequiredForImportDescription' />
                </h3>
                <Autocomplete
                  options={domainsOptions}
                  value={selectedDomain || null}
                  inputValue={selectedDomain ? selectedDomain.label : domainSearchText}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (option.label) return option.label;
                    if (option.value?.name) return option.value.name;
                    if (option.value?.hostname) return getDomainNameFromHostname(option.value.hostname);
                    return '';
                  }}
                  isOptionEqualToValue={(option, value) => option?.value?.hostname === value?.value?.hostname}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      variant="outlined" 
                      label={<ReduxLanguage languageKey="mks.hostnameRequired" />}
                      required
                      error={!selectedDomain}
                      helperText={''}
                      style={{ minWidth: 300 }}
                    />
                  )}
                  onInputChange={(event, newInputValue) => { 
                    this.setState({ domainSearchText: newInputValue }); 
                    const debouncedDomainSearch = _.debounce((text) => this.onFetchDomainsFromEmptyState(text), 500);
                    debouncedDomainSearch(newInputValue); 
                  }}
                  onChange={(event, newValue) => this.onSelectDomainFromEmptyState(newValue)}
                  noOptionsText="No domains found"
                  loadingText="Loading domains..."
                  clearOnBlur={false}
                  clearOnEscape={true}
                />
                <h3 style={{ marginTop: 20 }}>
                  <Link href="https://help-center.sofialocks.com/vasho-master-key-systems-management/" target="_blank" style={{ textDecoration: 'underline' }}>
                    {translations.t('forms.findOutMoreAboutThisFeature')}
                  </Link>
                </h3>
              </div>
            </Stack>
          )}
        </div>

        <CsvUploadModal
          open={showUploadModal}
          onClose={() => this.onCloseUploadModal()}
          onFileSelect={(file) => this.onFileSelect(file)}
          onFileFormatSelect={(fileFormat) => this.onFileFormatSelect(fileFormat)}
          onPreview={() => this.onPreviewCsv()}
          selectedFile={selectedFile}
          isLoading={isLoading}
          error={uploadError}
        />

        <CsvPreviewModal
          open={showPreviewModal}
          onClose={() => this.onClosePreviewModal()}
          onConfirm={() => this.onConfirmImport()}
          preview={hasPreview ? csvPreview : null}
          filename={selectedFile?.name || ''}
          isLoading={isLoading}
          lastImport={this.props.lastImport}
        />

        <CsvConfirmationModal
          open={showConfirmationModal}
          onClose={() => this.onCloseConfirmationModal()}
          onConfirm={() => this.onFinalConfirmImport()}
          filename={selectedFile?.name || ''}
          isLoading={isLoading}
        />
      </div>
    );
  }
}

export default withStyles(styles)(connector(MksImportsPage));
